import { Box } from '@material-ui/core';
import classNames from 'classnames';
import React, { FC, useContext } from 'react';
import { useShopConfigV2 } from '../../hooks/shopConfig';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useStyleType } from '../../hooks/useStyleType';
import { TextWithLineBreaks, TypographyWrapper } from '..';
import css from './SectionHeroV2.module.css';
import { getOptimizedImageSrcs } from '../../util/contentful';
import TopbarTransparencyContext from '../../context/TopbarTransparencyContext';

interface SectionHeroV2Props {
  actionEl?: React.ReactNode;
  bottomActionEl?: React.ReactNode;
  imageUrl?: string;
  mobileImageUrl?: string;
  shouldAnimate?: boolean;
}

const SectionHeroV2: FC<SectionHeroV2Props> = (props: SectionHeroV2Props) => {
  const { actionEl, bottomActionEl, imageUrl, mobileImageUrl, shouldAnimate = false } = props;

  const { copy, images, css: shopConfigV2Css } = useShopConfigV2();
  const isMobile = useIsMobile();
  const { isCondensedStyle } = useStyleType();
  const { isFullBleedTopbar } = useContext(TopbarTransparencyContext);

  const disableHeaderTint = shopConfigV2Css?.disableHeaderTint;
  const headerContentStyle = shopConfigV2Css?.headerContentStyle;
  const shouldUseH5ForSubheader = !!shopConfigV2Css?.h5;

  // If no mobile image is defined, use default image
  // optimized for mobile by height
  const heroImageDefault = getOptimizedImageSrcs({
    src: imageUrl || images?.heroImage?.url,
    height: isMobile ? '400' : undefined,
  });

  // If mobile image is defined, optimize mobile image by width
  const heroImageMobile = getOptimizedImageSrcs({
    src: mobileImageUrl || images?.heroImageMobile?.url,
    width: '600',
  });

  const optimizedImageSrcs =
    (isMobile && heroImageMobile.length > 0 && heroImageMobile) || heroImageDefault;

  const { heroTitle = '', heroSubtitle = '' } = copy || {};
  const isClientSide = typeof window !== 'undefined';

  const fallbackImageUrl = optimizedImageSrcs[optimizedImageSrcs.length - 1];
  const imageSrcSet = optimizedImageSrcs.join(', ') || undefined;

  return (
    <div className={css.root}>
      {!disableHeaderTint && <div className={css.imageOverlay} />}
      <img
        className={classNames(css.image, isFullBleedTopbar && css.fullBleedTopbarImage)}
        src={fallbackImageUrl}
        srcSet={imageSrcSet}
        fetchpriority="high"
        alt="shop header"
      />
      {/* Prevent animation from running twice */}
      {isClientSide && (
        <>
          <Box
            p={{ xs: '32px 16px', md: '64px 32px 32px' }}
            className={css.content}
            style={isMobile ? headerContentStyle?.mobile : headerContentStyle?.desktop}
          >
            <Box className={classNames({ [css.titleAnimation]: shouldAnimate })}>
              <TypographyWrapper variant="h6" component="h1">
                <TextWithLineBreaks text={heroTitle} />
              </TypographyWrapper>
            </Box>
            <Box
              mt={isCondensedStyle ? 0 : 1}
              className={classNames({ [css.subtitleAnimation]: shouldAnimate })}
            >
              <TypographyWrapper
                typographyOverrides={
                  shouldUseH5ForSubheader
                    ? undefined
                    : { style: { color: 'white', textWrap: 'balance' } }
                }
                variant={shouldUseH5ForSubheader ? 'h5' : isMobile ? 'body1' : 'h2'}
              >
                <TextWithLineBreaks text={heroSubtitle} />
              </TypographyWrapper>
            </Box>
            <Box mt={3} className={classNames({ [css.actionElAnimation]: shouldAnimate })}>
              {actionEl}
            </Box>
          </Box>
          {bottomActionEl && (
            <Box className={css.bottomActionElWrapper}>
              <Box className={classNames({ [css.bottomActionElAnimation]: shouldAnimate })}>
                {bottomActionEl}
              </Box>
            </Box>
          )}
        </>
      )}
    </div>
  );
};

export default SectionHeroV2;
